/* eslint-disable */
import {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import { DateRangePicker } from 'rsuite';
import {
	RefreshCcw, Loader2, ChevronDown, Check, Plus, X, Search, Trash2, Package,
} from 'lucide-react';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices, laneServices } from '@/Services';
import { getHigherUnitAndQuantity, unitMap, addCommas } from '@/Helpers';
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { useAuth } from '@/Components/Auth/Login/auth';
import { LANE_TYPES, LANE_TYPE_META, LANE_TYPE_LIST } from './laneTypes';

const ALL_STATUSES = ['PENDING', 'DISPATCH', 'TRANSIT'];
const TONNE_GRAMS = 1_000_000;
const MAX_ITEMS_ON_CARD = 5;
// Custom MIME type so row drop targets only react to order cards, not to
// files/text dragged in from elsewhere.
const DRAG_MIME = 'application/x-lane-po-id';

const resolveUnit = (rawUnit) => (rawUnit != null && rawUnit !== '' && !Number.isNaN(Number(rawUnit))
	? unitMap[Number(rawUnit)]
	: rawUnit);

const COUNT_UNITS = ['pc', 'count', 'piece', 'pieces', 'pcs'];
const isCountItem = (item) => COUNT_UNITS.includes(String(resolveUnit(item?.unit) || '').toLowerCase().trim());

const fmtCount = (n) => {
	const num = Number(n) || 0;
	return Number.isInteger(num) ? num : Number(num.toFixed(2));
};

const qtyLabel = (item) => {
	const unit = resolveUnit(item?.unit);
	const [qty, u] = getHigherUnitAndQuantity(unit, Number(item?.qty) || 0);
	return `×${qty}${u ? ` ${u}` : ''}`;
};

// Weight-unit -> grams. Only the units this system actually stores products in
// (see unitMap above); count/volume units have no meaningful tonnage.
const WEIGHT_UNIT_TO_GRAMS = {
	gram: 1, gm: 1, g: 1, grams: 1,
	kg: 1000, kilogram: 1000, kilograms: 1000,
};

// Sums an order's weight items in tonnes: the full ordered qty, or with
// pendingOnly just (qty - packaged).
const tonnageOf = (po, pendingOnly = false) => {
	const items = po?.items || [];
	const grams = items.reduce((acc, it) => {
		const unit = String(resolveUnit(it?.unit) || '').toLowerCase().trim();
		const gramsPerUnit = WEIGHT_UNIT_TO_GRAMS[unit];
		if (!gramsPerUnit) return acc;
		const qty = Number(it?.qty) || 0;
		const amount = pendingOnly ? Math.max(qty - (Number(it?.packaged) || 0), 0) : qty;
		return acc + amount * gramsPerUnit;
	}, 0);
	return grams / TONNE_GRAMS;
};

// Tiny non-zero loads would read "0.00 t", so under 10 kg fall back to kg.
const fmtTonnes = (t) => {
	if (t > 0 && t < 0.01) return `${parseFloat((t * 1000).toFixed(2))} kg`;
	return `${t.toFixed(t >= 10 ? 1 : 2)} t`;
};

const laneSort = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

// Popover combobox: pick an existing lane, or (allowCreate only) type a new
// name to create one. `value`/options are lane objects ({id, name}) fetched
// from the server; picking "Create" hands back {id: null, name} so the
// caller knows to create the lane server-side before assigning it. Lane
// *creation* and *removal* are gated to Internal/CXO Office/Config Admin — see canCreateLane
// in LanePlanningBoard (removal is only offered when onDeleteLane is passed);
// assigning an existing lane stays open to everyone.
const LaneSelect = ({
	value, options, onChange, allowCreate, onDeleteLane, laneType = LANE_TYPES.PACKING,
}) => {
	const meta = LANE_TYPE_META[laneType];
	const { Icon } = meta;
	const kind = meta.label.toLowerCase();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [confirmingId, setConfirmingId] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const trimmed = query.trim();
	const filtered = options.filter((o) => o.name.toLowerCase().includes(trimmed.toLowerCase()));
	const wouldCreate = trimmed && !options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase());
	const showCreate = allowCreate && wouldCreate;

	const confirmDelete = async (lane) => {
		setDeleting(true);
		try {
			await onDeleteLane(lane);
		} finally {
			setDeleting(false);
			setConfirmingId(null);
		}
	};

	return (
		<Popover
			open={open}
			onOpenChange={(v) => {
				setOpen(v);
				if (!v) { setQuery(''); setConfirmingId(null); }
			}}
		>
			<PopoverTrigger asChild>
				<button
					type='button'
					title={value ? `${meta.label}: ${value.name}` : `Assign ${meta.label}`}
					className={`flex max-w-[110px] flex-shrink-0 items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] font-semibold whitespace-nowrap ${value
						? meta.chipClass
						: 'border-gray-200 bg-gray-50 text-gray-500'}`}
				>
					<Icon size={11} className='flex-shrink-0' />
					<span className='truncate'>{value?.name || 'Assign'}</span>
					<ChevronDown size={11} className='flex-shrink-0' />
				</button>
			</PopoverTrigger>
			<PopoverContent align='end' className='w-56 rounded-lg border-gray-200 p-2 shadow-lg'>
				<p className='m-0 mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500'>
					<Icon size={11} /> {meta.label}
				</p>
				<div className='relative mb-2'>
					<Search size={12} className='absolute left-2 top-1/2 -translate-y-1/2 text-gray-400' />
					<input
						autoFocus
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={allowCreate ? `Search or create ${kind}…` : `Search ${kind}…`}
						className='w-full rounded-md border border-gray-200 py-1 pl-6 pr-2 text-[11px] focus:border-teal-400 focus:outline-none'
					/>
				</div>
				<div className='flex max-h-40 flex-col gap-0.5 overflow-y-auto'>
					{value && (
						<button
							type='button'
							onClick={() => { onChange(null); setOpen(false); }}
							className='flex items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-semibold text-gray-500 hover:bg-gray-50'
						>
							<X size={11} /> Unassign
						</button>
					)}
					{filtered.map((lane) => (confirmingId === lane.id ? (
						<div key={lane.id} className='rounded border border-red-200 bg-red-50 p-2'>
							<p className='m-0 text-[11px] font-semibold text-red-700'>Remove "{lane.name}"?</p>
							<p className='m-0 mt-0.5 text-[10px] text-red-600'>Its orders lose this {kind}.</p>
							<div className='mt-1.5 flex gap-1.5'>
								<button
									type='button'
									disabled={deleting}
									onClick={() => confirmDelete(lane)}
									className='rounded bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-red-700 disabled:opacity-50'
								>
									{deleting ? 'Removing…' : 'Remove'}
								</button>
								<button
									type='button'
									disabled={deleting}
									onClick={() => setConfirmingId(null)}
									className='rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-600 hover:bg-gray-50'
								>
									Cancel
								</button>
							</div>
						</div>
					) : (
						<div key={lane.id} className='flex items-center gap-0.5 rounded hover:bg-gray-50'>
							{onDeleteLane && (
								<button
									type='button'
									title={`Remove ${kind}`}
									onClick={() => setConfirmingId(lane.id)}
									className='flex-shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600'
								>
									<Trash2 size={11} />
								</button>
							)}
							<button
								type='button'
								onClick={() => { onChange(lane); setOpen(false); }}
								className={`flex min-w-0 flex-1 items-center justify-between gap-1.5 rounded py-1 pr-2 text-left text-[11px] font-medium ${onDeleteLane ? '' : 'pl-2'} ${lane.id === value?.id ? 'text-teal-700' : 'text-gray-700'}`}
							>
								<span className='truncate'>{lane.name}</span>
								{lane.id === value?.id && <Check size={11} className='flex-shrink-0' />}
							</button>
						</div>
					)))}
					{showCreate && (
						<button
							type='button'
							onClick={() => { onChange({ id: null, name: trimmed }); setOpen(false); }}
							className='flex items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-bold text-teal-700 hover:bg-teal-50'
						>
							<Plus size={11} /> Create "{trimmed}"
						</button>
					)}
					{!filtered.length && !showCreate && (
						<span className='px-2 py-1 text-[11px] text-gray-400'>
							{wouldCreate && !allowCreate
								? `Only Internal / L4 users can add a ${kind}`
								: allowCreate
									? `Type to create a ${kind}`
									: `No matching ${kind}s`}
						</span>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

// Left-border colour by stage, same as the Orders Board columns: orange = in
// production (nothing packed), green = packing started, dark = ready to
// dispatch, indigo = dispatched.
const cardBorderClassOf = (po) => {
	const status = String(po?.orderStatus || '').toUpperCase();
	if (status === 'TRANSIT') return 'border-l-indigo-400';
	if (status === 'DISPATCH') return 'border-l-gray-700';
	return (po?.items || []).some((it) => Number(it?.packaged) > 0)
		? 'border-l-green-500'
		: 'border-l-orange-400';
};

// Card layout mirrors the Orders Board OrderCard; the tonnage line and the lane
// dropdowns are the Lane Planning additions. lanes: the shared lane context
// built in LanePlanningBoard - one dropdown per lane type, packing first.
const LaneOrderCard = ({ po, lanes, canDrag }) => {
	const [expanded, setExpanded] = useState(false);
	const [dragging, setDragging] = useState(false);
	const items = po?.items || [];
	const visibleItems = expanded ? items : items.slice(0, MAX_ITEMS_ON_CARD);
	const hiddenCount = items.length - MAX_ITEMS_ON_CARD;
	const who = po?.vendorName || po?.storeName;
	const dispatchBy = po?.scheduledAt ?? po?.dispatchBy ?? po?.createdAt;
	const amount = po?.finalValue;
	const totalTonnage = tonnageOf(po);
	const pendingTonnage = tonnageOf(po, true);

	return (
		<div
			draggable={canDrag}
			onDragStart={canDrag ? (e) => {
				e.dataTransfer.setData(DRAG_MIME, String(po?.id));
				e.dataTransfer.effectAllowed = 'move';
				setDragging(true);
			} : undefined}
			onDragEnd={canDrag ? () => setDragging(false) : undefined}
			className={`w-[320px] flex-shrink-0 rounded-xl border border-l-4 border-gray-200 bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md ${cardBorderClassOf(po)} ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging ? 'opacity-40' : ''}`}
		>
			<div className='flex items-center gap-1.5'>
				<span className='whitespace-nowrap text-[12px] font-extrabold text-gray-900'>
					#{po?.clientPoId || po?.id}
				</span>
				{who && (
					<span className='truncate rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600'>
						{who}
					</span>
				)}
			</div>

			<div className='mt-2.5 flex flex-col gap-2'>
				{visibleItems.map((item, i) => {
					const countable = isCountItem(item);
					return (
						<div key={item?.poItemId ?? item?.id ?? i} className='flex items-center justify-between gap-2'>
							<div className='flex min-w-0 items-center gap-1.5'>
								<span className='flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-amber-50 text-amber-700'>
									<Package size={10} />
								</span>
								<span className='truncate text-[11px] font-medium text-gray-800'>
									{countable && (
										<b className='mr-1 whitespace-nowrap font-extrabold text-gray-900'>
											{fmtCount(item?.qty)} ×
										</b>
									)}
									{item?.productName}
									{item?.variantName ? ` (${item.variantName})` : ''}
								</span>
							</div>
							{!countable && (
								<span className='whitespace-nowrap text-[10px] font-semibold text-gray-500'>
									{qtyLabel(item)}
								</span>
							)}
						</div>
					);
				})}
				{hiddenCount > 0 && (
					<button
						type='button'
						onClick={() => setExpanded((prev) => !prev)}
						className='self-start text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline'
					>
						{expanded ? 'Show less' : `+${hiddenCount} more item${hiddenCount > 1 ? 's' : ''}`}
					</button>
				)}
				{items.length === 0 && <span className='text-[10px] text-gray-400'>No items</span>}
			</div>

			<div className='mt-2.5 flex flex-col gap-2 border-t border-gray-100 pt-2'>
				<div className='flex items-center justify-between'>
					<span className='text-[10px] font-bold text-gray-700'>
						{dispatchBy ? `Dispatch by · ${moment(dispatchBy).format('DD MMM, hh:mm A')}` : ''}
					</span>
					<span className='text-[12px] font-extrabold text-gray-900'>
						{amount !== null && amount !== undefined && amount >= 0
							? `₹${addCommas(amount)}`
							: `${items.length} item${items.length !== 1 ? 's' : ''}`}
					</span>
				</div>
				<div className='flex items-center justify-between gap-2'>
					<div className='flex min-w-0 flex-col'>
						{totalTonnage > 0 ? (
							<>
								<span className='text-[11px] font-extrabold text-gray-900'>
									{fmtTonnes(totalTonnage)}
									<span className='ml-1 text-[10px] font-semibold text-gray-400'>total</span>
								</span>
								<span className='text-[10px] font-bold text-gray-500'>
									{pendingTonnage > 0 ? `${fmtTonnes(pendingTonnage)} pending` : 'Fully packed'}
								</span>
							</>
						) : (
							<span className='text-[10px] font-bold text-gray-400'>No weight items</span>
						)}
					</div>
					<div className='flex flex-shrink-0 items-center gap-1'>
						{LANE_TYPE_LIST.map((type) => (
							<LaneSelect
								key={type}
								laneType={type}
								value={lanes.laneOf(po?.id, type)}
								options={lanes.lanesByType[type] || []}
								onChange={(lane) => lanes.onLaneChange(po?.id, lane, type)}
								allowCreate={lanes.allowCreate}
								onDeleteLane={lanes.allowCreate ? (lane) => lanes.onDeleteLane(lane, type) : undefined}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

// One row of the board: the orders in one lane of the grouped-by type (laneType),
// or the orders with no lane of that type (isUnassigned).
const LaneRow = ({
	title, orders, currentLane, laneType, lanes, isUnassigned, canDrag, onDropOrder,
}) => {
	const { Icon } = LANE_TYPE_META[laneType];
	const [isOver, setIsOver] = useState(false);
	const tonnage = orders.reduce((acc, po) => acc + tonnageOf(po, true), 0);
	const acceptsDrag = (e) => canDrag && Array.from(e.dataTransfer?.types || []).includes(DRAG_MIME);

	return (
		<div
			onDragOver={(e) => {
				if (!acceptsDrag(e)) return;
				e.preventDefault();
				e.dataTransfer.dropEffect = 'move';
				if (!isOver) setIsOver(true);
			}}
			onDragLeave={(e) => {
				if (!e.currentTarget.contains(e.relatedTarget)) setIsOver(false);
			}}
			onDrop={(e) => {
				if (!acceptsDrag(e)) return;
				e.preventDefault();
				setIsOver(false);
				onDropOrder(e.dataTransfer.getData(DRAG_MIME), currentLane);
			}}
			className={`flex flex-col gap-2.5 rounded-lg border-b border-gray-200 pb-4 transition-colors ${isOver ? 'bg-teal-50 ring-2 ring-teal-300' : ''}`}
		>
			<div className='flex flex-wrap items-center gap-2'>
				<span
					className={`flex items-center gap-1.5 text-[13px] font-extrabold ${isUnassigned ? 'text-gray-500' : 'text-gray-900'}`}
					title={isUnassigned ? `No ${LANE_TYPE_META[laneType].label.toLowerCase()}` : LANE_TYPE_META[laneType].label}
				>
					<Icon size={14} className={isUnassigned ? 'text-gray-400' : 'text-gray-700'} />
					{title}
				</span>
				<span className='rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600'>
					{orders.length} order{orders.length !== 1 ? 's' : ''}
				</span>
				{!isUnassigned && (
					<span className='rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700'>
						{fmtTonnes(tonnage)} pending packaging
					</span>
				)}
			</div>
			{orders.length > 0 ? (
				<div className='flex gap-3 overflow-x-auto pb-1'>
					{orders.map((po) => (
						<LaneOrderCard
							key={po?.id}
							po={po}
							lanes={lanes}
							canDrag={canDrag}
						/>
					))}
				</div>
			) : (
				<div className='rounded-lg border border-dashed border-gray-200 py-4 text-center text-[11px] text-gray-400'>
					{canDrag ? 'No orders — drag an order here' : 'No orders'}
				</div>
			)}
		</div>
	);
};

const LanePlanningBoard = () => {
	const { userConfig } = useAuth();
	const userRoles = Object.values(userConfig?.userRolesDict ?? {});
	// Lane creation and removal: Internal, CXO Office and Config Admin roles only.
	const canCreateLane = ['INTERNAL', 'Internal', 'CXO Office', 'Config Admin']
		.some((role) => userRoles.includes(role));

	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dateRange, setDateRange] = useState([moment().toDate(), moment().toDate()]);
	const [activeTab, setActiveTab] = useState('pending');
	// Per lane type (PACKING / STORAGE): lanesByType - the client's full lane list
	// (server-side, independent of date range); assignmentsByType - poId -> laneId.
	const [lanesByType, setLanesByType] = useState({ [LANE_TYPES.PACKING]: [], [LANE_TYPES.STORAGE]: [] });
	const [assignmentsByType, setAssignmentsByType] = useState({ [LANE_TYPES.PACKING]: {}, [LANE_TYPES.STORAGE]: {} });
	// Which lane type the rows are grouped by. Packing by default.
	const [groupBy, setGroupBy] = useState(LANE_TYPES.PACKING);
	const lanes = lanesByType[groupBy] || [];
	const assignments = assignmentsByType[groupBy] || {};
	const setLanesOf = (type, update) => setLanesByType((prev) => ({ ...prev, [type]: update(prev[type] || []) }));
	const setAssignmentsOf = (type, update) => setAssignmentsByType((prev) => ({ ...prev, [type]: update(prev[type] || {}) }));

	const fetchOrders = useCallback(async (range) => {
		setLoading(true);
		try {
			const res = await inventoryAnalyticsServices.fetchPoList({
				req: {
					data: {
						filters: {
							fromStoreId: null,
							status: ALL_STATUSES,
							buyers: [],
							variantIds: [],
							orderType: 'buyers',
							userOrderType: 'sales',
							dateRangeFilter: {
								dateType: '',
								from: moment(range?.[0]).format('YYYY-MM-DD'),
								to: moment(range?.[1]).format('YYYY-MM-DD'),
							},
						},
						showInternalData: false,
					},
				},
			});
			if (res?.statusCode === 200) {
				setOrders(res?.data || []);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to load B2B orders.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: typeof error === 'string' ? error : 'Failed to load B2B orders.',
			});
		} finally {
			setLoading(false);
		}
	}, []);

	const loadLanes = useCallback(async () => {
		await Promise.all(LANE_TYPE_LIST.map(async (type) => {
			try {
				const res = await laneServices.fetchLanes(type);
				if (res?.statusCode === 200) {
					const sorted = (res?.data || []).slice().sort((a, b) => laneSort(a?.name || '', b?.name || ''));
					setLanesByType((prev) => ({ ...prev, [type]: sorted }));
				}
			} catch (error) {
				// laneServices already surfaces a toast via handleResponse
			}
		}));
	}, []);

	const loadAssignments = useCallback(async () => {
		await Promise.all(LANE_TYPE_LIST.map(async (type) => {
			try {
				const res = await laneServices.fetchLaneAssignments(type);
				if (res?.statusCode === 200) {
					const map = {};
					(res?.data || []).forEach((a) => {
						if (a?.poId != null && a?.laneId != null) map[a.poId] = a.laneId;
					});
					setAssignmentsByType((prev) => ({ ...prev, [type]: map }));
				}
			} catch (error) {
				// laneServices already surfaces a toast via handleResponse
			}
		}));
	}, []);

	useEffect(() => {
		loadLanes();
		loadAssignments();
	}, [loadLanes, loadAssignments]);

	useEffect(() => {
		fetchOrders(dateRange);
	}, [dateRange, fetchOrders]);

	// laneValue is a lane object picked from LaneSelect: null to unassign, an
	// existing {id, name} to assign, or {id: null, name} for a not-yet-created
	// lane (LaneSelect only offers that option when allowCreate is true).
	// laneType says which of the order's lanes changes; the other is untouched.
	const handleLaneChange = async (orderId, laneValue, laneType = groupBy) => {
		try {
			if (!laneValue) {
				const res = await laneServices.unassignLane(orderId, laneType);
				if (res?.statusCode === 200) {
					setAssignmentsOf(laneType, (prev) => {
						const next = { ...prev };
						delete next[orderId];
						return next;
					});
				}
				return;
			}

			let lane = laneValue;
			if (!lane.id) {
				const createRes = await laneServices.createLane(lane.name, laneType);
				if (createRes?.statusCode !== 200 || !createRes?.data?.id) return;
				lane = createRes.data;
				setLanesOf(laneType, (prev) => (prev.some((l) => l.id === lane.id)
					? prev
					: [...prev, lane].sort((a, b) => laneSort(a?.name || '', b?.name || ''))));
			}

			const assignRes = await laneServices.assignLane(orderId, lane.id);
			if (assignRes?.statusCode === 200) {
				setAssignmentsOf(laneType, (prev) => ({ ...prev, [orderId]: lane.id }));
			}
		} catch (error) {
			// laneServices already surfaces a toast via handleResponse
		}
	};

	// Soft-deletes the lane server-side, which also unassigns every order in it;
	// mirror that locally so the cards drop into Unassigned straight away.
	const handleDeleteLane = async (lane, laneType = groupBy) => {
		try {
			const res = await laneServices.deleteLane(lane.id);
			if (res?.statusCode === 200) {
				setLanesOf(laneType, (prev) => prev.filter((l) => l.id !== lane.id));
				setAssignmentsOf(laneType, (prev) => {
					const next = {};
					Object.keys(prev).forEach((poId) => {
						if (prev[poId] !== lane.id) next[poId] = prev[poId];
					});
					return next;
				});
				notificationServices.generateNotification({
					type: 'success',
					message: `${LANE_TYPE_META[laneType].label} "${lane.name}" removed`,
				});
			}
		} catch (error) {
			// laneServices already surfaces a toast via handleResponse
		}
	};

	// targetLane is the row's lane object, or null for the Unassigned row.
	const handleDropOrder = (droppedPoId, targetLane) => {
		const po = orders.find((p) => String(p?.id) === String(droppedPoId));
		if (!po) return;
		const currentLaneId = assignments[po.id] ?? null;
		if (currentLaneId === (targetLane?.id ?? null)) return;
		handleLaneChange(po.id, targetLane);
	};

	const pendingOrders = useMemo(
		() => (orders || []).filter((po) => String(po?.orderStatus || '').toUpperCase() !== 'TRANSIT'),
		[orders]
	);
	const dispatchedOrders = useMemo(
		() => (orders || []).filter((po) => String(po?.orderStatus || '').toUpperCase() === 'TRANSIT'),
		[orders]
	);
	const activeOrders = activeTab === 'pending' ? pendingOrders : dispatchedOrders;
	// Only pending orders can be moved between lanes; dispatched ones are history.
	const canDrag = activeTab === 'pending';

	// laneType -> laneId -> lane
	const laneByIdByType = useMemo(() => {
		const out = {};
		LANE_TYPE_LIST.forEach((type) => {
			const map = {};
			(lanesByType[type] || []).forEach((l) => { if (l?.id != null) map[l.id] = l; });
			out[type] = map;
		});
		return out;
	}, [lanesByType]);
	const laneById = laneByIdByType[groupBy] || {};

	// What every card needs to show and change both of its lanes.
	const laneCtx = {
		lanesByType,
		laneOf: (poId, type) => laneByIdByType[type]?.[assignmentsByType[type]?.[poId]] || null,
		onLaneChange: handleLaneChange,
		onDeleteLane: handleDeleteLane,
		allowCreate: canCreateLane,
	};

	const laneGroups = useMemo(() => {
		const byLane = {}; // laneId -> orders
		const unassigned = [];
		activeOrders.forEach((po) => {
			const lane = laneById[assignments[po?.id]];
			if (lane) {
				if (!byLane[lane.id]) byLane[lane.id] = [];
				byLane[lane.id].push(po);
			} else {
				unassigned.push(po);
			}
		});
		// Pending tab lists every lane, even empty ones, so there is always a
		// row to drop an order onto; dispatched only shows lanes that have orders.
		const visibleLanes = canDrag ? lanes : lanes.filter((l) => byLane[l.id]);
		return {
			unassigned,
			lanes: visibleLanes.map((lane) => ({ lane, orders: byLane[lane.id] || [] })),
		};
	}, [activeOrders, assignments, laneById, lanes, canDrag]);

	return (
		<div className='flex w-full flex-col gap-3 bg-white p-4 text-left'>
			<div className='flex flex-wrap items-center gap-3'>
				<p className='m-0 text-[20px] font-bold text-gray-900'>Lane Planning</p>
				<div className='ml-2 flex items-center gap-2'>
					<span className='text-[11px] font-bold text-gray-500'>Scheduled Date</span>
					<DateRangePicker
						value={dateRange}
						onChange={(value) => { if (value?.[0] && value?.[1]) setDateRange(value); }}
						format='dd-MM-yyyy'
						isoWeek
						cleanable={false}
						size='sm'
						style={{ width: 230 }}
						menuStyle={{ zIndex: 1100 }}
						placeholder='Start Date  --  End Date'
					/>
				</div>
				<button
					type='button'
					onClick={() => { fetchOrders(dateRange); loadLanes(); loadAssignments(); }}
					title='Refresh'
					className='ml-auto flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 hover:bg-gray-50'
				>
					<RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
				</button>
			</div>

			<div className='flex items-center gap-6 border-b border-gray-200'>
				{[
					{ key: 'pending', label: 'Pending', count: pendingOrders.length },
					{ key: 'dispatched', label: 'Dispatched', count: dispatchedOrders.length },
				].map((tab) => (
					<button
						key={tab.key}
						type='button'
						onClick={() => setActiveTab(tab.key)}
						className={`flex items-center gap-1.5 border-b-2 pb-1.5 text-[13px] transition-colors ${activeTab === tab.key
							? 'border-gray-900 font-bold text-gray-900'
							: 'border-transparent font-semibold text-gray-400 hover:text-gray-600'}`}
					>
						{tab.label}
						<span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${activeTab === tab.key
							? 'bg-gray-200 text-gray-800'
							: 'bg-gray-100 text-gray-400'}`}
						>
							{tab.count}
						</span>
					</button>
				))}
				<div className='mb-1.5 ml-auto flex items-center gap-2'>
					<span className='text-[11px] font-bold text-gray-500'>Group by</span>
					<div className='flex items-center gap-1 rounded-lg bg-gray-100 p-1'>
						{LANE_TYPE_LIST.map((type) => {
							const { Icon, label } = LANE_TYPE_META[type];
							return (
								<button
									key={type}
									type='button'
									onClick={() => setGroupBy(type)}
									className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-[12px] transition-all ${groupBy === type
										? 'bg-white font-bold text-gray-900 shadow-sm'
										: 'font-semibold text-gray-500 hover:text-gray-700'}`}
								>
									<Icon size={13} />
									{label}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			<div className='flex flex-col gap-4 overflow-y-auto pb-6'>
				<LaneRow
					title={`No ${LANE_TYPE_META[groupBy].label}`}
					orders={laneGroups.unassigned}
					currentLane={null}
					laneType={groupBy}
					lanes={laneCtx}
					canDrag={canDrag}
					onDropOrder={handleDropOrder}
					isUnassigned
				/>
				{laneGroups.lanes.map(({ lane, orders: laneOrders }) => (
					<LaneRow
						key={`${groupBy}-${lane.id}`}
						title={lane.name}
						orders={laneOrders}
						currentLane={lane}
						laneType={groupBy}
						lanes={laneCtx}
						canDrag={canDrag}
						onDropOrder={handleDropOrder}
					/>
				))}
				{!loading && activeOrders.length === 0 && (
					<div className='flex items-center justify-center py-16 text-[12px] text-gray-400'>
						No {activeTab === 'pending' ? 'pending' : 'dispatched'} orders for the selected date range
					</div>
				)}
			</div>

			{loading && (
				<div className='fixed inset-0 z-[1200] flex items-center justify-center bg-white/40 backdrop-blur-[1px]'>
					<Loader2 size={30} className='animate-spin text-amber-500' />
				</div>
			)}
		</div>
	);
};

export default LanePlanningBoard;
